import React, { useState } from 'react';
import { useTheme } from '@mui/material/styles';
import {
    Box,
    Card,
    CardContent,
    Grid,
    Typography,
    Button,
    TextField,
    Switch,
    FormControlLabel,
    Alert,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    InputAdornment,
    Slider,
    Chip,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Tabs,
    Tab,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    LinearProgress
} from '@mui/material';

// icons
import {
    IconShield,
    IconKey,
    IconDatabase,
    IconTrash,
    IconPlus,
    IconEdit,
    IconCheck,
    IconChevronDown,
    IconLock,
    IconFileZip,
    IconClock,
    IconMail,
    IconWebhook
} from '@tabler/icons-react';

const StorageSettings = () => {
    const theme = useTheme();
    const [tabValue, setTabValue] = useState(0);
    const [quotaDialog, setQuotaDialog] = useState(false);
    const [retentionDialog, setRetentionDialog] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);

    // Settings state
    const [settings, setSettings] = useState({
        autoBackup: true,
        backupFrequency: 'daily',
        fileVersioning: true,
        versionLimit: 5,
        compression: false,
        compressionLevel: 50,
        encryption: true,
        encryptionType: 'aes256',
        maxFileSize: 100,
        allowedExtensions: '.jpg,.jpeg,.png,.gif,.pdf,.doc,.docx,.xls,.xlsx,.zip,.tar,.gz',
        storageQuota: 50,
        retentionDays: 30,
        publicSharing: false,
        virusScanning: true,
        duplicateDetection: true
    });

    // User quotas
    const userQuotas = [
        { id: 1, user: 'john.doe@example.com', allocated: 10, used: 7.5, files: 1247 },
        { id: 2, user: 'jane.smith@example.com', allocated: 15, used: 12.3, files: 2156 },
        { id: 3, user: 'mike.johnson@example.com', allocated: 20, used: 8.9, files: 892 }
    ];

    // Retention policies
    const retentionPolicies = [
        { id: 1, name: 'Archive Old Files', condition: 'Last accessed > 90 days', action: 'Move to cold storage', status: 'active' },
        { id: 2, name: 'Delete Temporary Files', condition: 'File path contains /tmp/', action: 'Delete after 7 days', status: 'active' },
        { id: 3, name: 'Compress Large Files', condition: 'File size > 500MB', action: 'Compress to ZIP', status: 'inactive' }
    ];

    // Notification settings
    const notifications = [
        { id: 1, event: 'Storage quota exceeded', email: true, webhook: false, inApp: true },
        { id: 2, event: 'Backup completed', email: false, webhook: true, inApp: true },
        { id: 3, event: 'File virus detected', email: true, webhook: true, inApp: true },
        { id: 4, event: 'Large file uploaded', email: false, webhook: false, inApp: true }
    ];

    const handleSettingChange = (setting, value) => {
        setSettings((prev) => ({ ...prev, [setting]: value }));
    };

    return (
        <Box>
            <Card>
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
                        <Tab label="General Settings" />
                        <Tab label="Security & Privacy" />
                        <Tab label="Quotas & Limits" />
                        <Tab label="Retention Policies" />
                        <Tab label="Notifications" />
                    </Tabs>
                </Box>

                {/* General Settings Tab */}
                {tabValue === 0 && (
                    <CardContent>
                        <Typography variant="h4" gutterBottom>
                            General Storage Settings
                        </Typography>

                        <Grid container spacing={3}>
                            {/* Backup Settings */}
                            <Grid item xs={12}>
                                <Accordion defaultExpanded>
                                    <AccordionSummary expandIcon={<IconChevronDown />}>
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <IconDatabase />
                                            <Typography variant="h5">Backup Configuration</Typography>
                                        </Box>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12}>
                                                <FormControlLabel
                                                    control={
                                                        <Switch
                                                            checked={settings.autoBackup}
                                                            onChange={(e) => handleSettingChange('autoBackup', e.target.checked)}
                                                        />
                                                    }
                                                    label="Enable automatic backup"
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <FormControl fullWidth disabled={!settings.autoBackup}>
                                                    <InputLabel>Backup Frequency</InputLabel>
                                                    <Select
                                                        value={settings.backupFrequency}
                                                        label="Backup Frequency"
                                                        onChange={(e) => handleSettingChange('backupFrequency', e.target.value)}
                                                    >
                                                        <MenuItem value="hourly">Hourly</MenuItem>
                                                        <MenuItem value="daily">Daily</MenuItem>
                                                        <MenuItem value="weekly">Weekly</MenuItem>
                                                        <MenuItem value="monthly">Monthly</MenuItem>
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <TextField
                                                    fullWidth
                                                    label="Backup Location"
                                                    defaultValue="/backups/storage/"
                                                    disabled={!settings.autoBackup}
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <IconDatabase size={18} />
                                                            </InputAdornment>
                                                        )
                                                    }}
                                                />
                                            </Grid>
                                        </Grid>
                                    </AccordionDetails>
                                </Accordion>
                            </Grid>

                            {/* File Versioning */}
                            <Grid item xs={12}>
                                <Accordion>
                                    <AccordionSummary expandIcon={<IconChevronDown />}>
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <IconClock />
                                            <Typography variant="h5">File Versioning</Typography>
                                        </Box>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12}>
                                                <FormControlLabel
                                                    control={
                                                        <Switch
                                                            checked={settings.fileVersioning}
                                                            onChange={(e) => handleSettingChange('fileVersioning', e.target.checked)}
                                                        />
                                                    }
                                                    label="Keep previous versions of files"
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Typography gutterBottom>Maximum versions to keep: {settings.versionLimit}</Typography>
                                                <Slider
                                                    value={settings.versionLimit}
                                                    onChange={(e, value) => handleSettingChange('versionLimit', value)}
                                                    min={1}
                                                    max={20}
                                                    marks
                                                    disabled={!settings.fileVersioning}
                                                    valueLabelDisplay="auto"
                                                />
                                            </Grid>
                                        </Grid>
                                    </AccordionDetails>
                                </Accordion>
                            </Grid>

                            {/* Compression */}
                            <Grid item xs={12}>
                                <Accordion>
                                    <AccordionSummary expandIcon={<IconChevronDown />}>
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <IconFileZip />
                                            <Typography variant="h5">Compression</Typography>
                                        </Box>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12}>
                                                <FormControlLabel
                                                    control={
                                                        <Switch
                                                            checked={settings.compression}
                                                            onChange={(e) => handleSettingChange('compression', e.target.checked)}
                                                        />
                                                    }
                                                    label="Enable automatic compression"
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Typography gutterBottom>Compression Level: {settings.compressionLevel}%</Typography>
                                                <Slider
                                                    value={settings.compressionLevel}
                                                    onChange={(e, value) => handleSettingChange('compressionLevel', value)}
                                                    min={0}
                                                    max={100}
                                                    disabled={!settings.compression}
                                                    valueLabelDisplay="auto"
                                                />
                                                <Box display="flex" justifyContent="space-between">
                                                    <Typography variant="caption">Faster</Typography>
                                                    <Typography variant="caption">Smaller Size</Typography>
                                                </Box>
                                            </Grid>
                                        </Grid>
                                    </AccordionDetails>
                                </Accordion>
                            </Grid>
                        </Grid>
                    </CardContent>
                )}

                {/* Security & Privacy Tab */}
                {tabValue === 1 && (
                    <CardContent>
                        <Typography variant="h4" gutterBottom>
                            Security & Privacy Settings
                        </Typography>

                        <Grid container spacing={3}>
                            {/* Encryption */}
                            <Grid item xs={12} md={6}>
                                <Card variant="outlined">
                                    <CardContent>
                                        <Box display="flex" alignItems="center" gap={1} mb={2}>
                                            <IconLock />
                                            <Typography variant="h5">Encryption</Typography>
                                        </Box>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={settings.encryption}
                                                    onChange={(e) => handleSettingChange('encryption', e.target.checked)}
                                                />
                                            }
                                            label="Encrypt files at rest"
                                            sx={{ mb: 2 }}
                                        />
                                        <FormControl fullWidth disabled={!settings.encryption}>
                                            <InputLabel>Encryption Type</InputLabel>
                                            <Select
                                                value={settings.encryptionType}
                                                label="Encryption Type"
                                                onChange={(e) => handleSettingChange('encryptionType', e.target.value)}
                                            >
                                                <MenuItem value="aes128">AES-128</MenuItem>
                                                <MenuItem value="aes256">AES-256</MenuItem>
                                                <MenuItem value="rsa2048">RSA-2048</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </CardContent>
                                </Card>
                            </Grid>

                            {/* Access Control */}
                            <Grid item xs={12} md={6}>
                                <Card variant="outlined">
                                    <CardContent>
                                        <Box display="flex" alignItems="center" gap={1} mb={2}>
                                            <IconShield />
                                            <Typography variant="h5">Access Control</Typography>
                                        </Box>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={settings.publicSharing}
                                                    onChange={(e) => handleSettingChange('publicSharing', e.target.checked)}
                                                />
                                            }
                                            label="Allow public file sharing"
                                            sx={{ mb: 2 }}
                                        />
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={settings.virusScanning}
                                                    onChange={(e) => handleSettingChange('virusScanning', e.target.checked)}
                                                />
                                            }
                                            label="Enable virus scanning"
                                            sx={{ mb: 2 }}
                                        />
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={settings.duplicateDetection}
                                                    onChange={(e) => handleSettingChange('duplicateDetection', e.target.checked)}
                                                />
                                            }
                                            label="Detect duplicate files"
                                        />
                                    </CardContent>
                                </Card>
                            </Grid>

                            {/* API Keys */}
                            <Grid item xs={12}>
                                <Card variant="outlined">
                                    <CardContent>
                                        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                                            <Box display="flex" alignItems="center" gap={1}>
                                                <IconKey />
                                                <Typography variant="h5">API Keys</Typography>
                                            </Box>
                                            <Button startIcon={<IconPlus />} variant="contained" size="small">
                                                Generate Key
                                            </Button>
                                        </Box>
                                        <TableContainer>
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>Key Name</TableCell>
                                                        <TableCell>Key</TableCell>
                                                        <TableCell>Created</TableCell>
                                                        <TableCell>Last Used</TableCell>
                                                        <TableCell align="right">Actions</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    <TableRow>
                                                        <TableCell>Production API</TableCell>
                                                        <TableCell>sk_live_••••••••••••</TableCell>
                                                        <TableCell>2024-01-01</TableCell>
                                                        <TableCell>2 hours ago</TableCell>
                                                        <TableCell align="right">
                                                            <IconButton size="small" color="error">
                                                                <IconTrash size={16} />
                                                            </IconButton>
                                                        </TableCell>
                                                    </TableRow>
                                                    <TableRow>
                                                        <TableCell>Development API</TableCell>
                                                        <TableCell>sk_test_••••••••••••</TableCell>
                                                        <TableCell>2024-01-10</TableCell>
                                                        <TableCell>Yesterday</TableCell>
                                                        <TableCell align="right">
                                                            <IconButton size="small" color="error">
                                                                <IconTrash size={16} />
                                                            </IconButton>
                                                        </TableCell>
                                                    </TableRow>
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                    </CardContent>
                )}

                {/* Quotas & Limits Tab */}
                {tabValue === 2 && (
                    <CardContent>
                        <Typography variant="h4" gutterBottom>
                            Storage Quotas & Limits
                        </Typography>

                        <Grid container spacing={3}>
                            {/* Global Settings */}
                            <Grid item xs={12} md={6}>
                                <Card variant="outlined">
                                    <CardContent>
                                        <Typography variant="h5" gutterBottom>
                                            Global Limits
                                        </Typography>
                                        <Box sx={{ mb: 3 }}>
                                            <Typography gutterBottom>Maximum file size: {settings.maxFileSize} MB</Typography>
                                            <Slider
                                                value={settings.maxFileSize}
                                                onChange={(e, value) => handleSettingChange('maxFileSize', value)}
                                                min={1}
                                                max={1000}
                                                valueLabelDisplay="auto"
                                            />
                                        </Box>
                                        <Box sx={{ mb: 3 }}>
                                            <Typography gutterBottom>Total storage quota: {settings.storageQuota} GB</Typography>
                                            <Slider
                                                value={settings.storageQuota}
                                                onChange={(e, value) => handleSettingChange('storageQuota', value)}
                                                min={10}
                                                max={1000}
                                                step={10}
                                                valueLabelDisplay="auto"
                                            />
                                        </Box>
                                        <TextField
                                            fullWidth
                                            multiline
                                            rows={3}
                                            label="Allowed file extensions"
                                            value={settings.allowedExtensions}
                                            onChange={(e) => handleSettingChange('allowedExtensions', e.target.value)}
                                            helperText="Comma-separated list"
                                        />
                                    </CardContent>
                                </Card>
                            </Grid>

                            {/* User Quotas */}
                            <Grid item xs={12} md={6}>
                                <Card variant="outlined">
                                    <CardContent>
                                        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                                            <Typography variant="h5">User Quotas</Typography>
                                            <Button startIcon={<IconPlus />} size="small" onClick={() => setQuotaDialog(true)}>
                                                Add User
                                            </Button>
                                        </Box>
                                        <List>
                                            {userQuotas.map((user) => (
                                                <ListItem key={user.id}>
                                                    <ListItemText
                                                        primary={user.user}
                                                        secondary={
                                                            <Box>
                                                                <LinearProgress
                                                                    variant="determinate"
                                                                    value={(user.used / user.allocated) * 100}
                                                                    sx={{ mt: 1, mb: 0.5 }}
                                                                />
                                                                <Typography variant="caption">
                                                                    {user.used} GB / {user.allocated} GB • {user.files} files
                                                                </Typography>
                                                            </Box>
                                                        }
                                                    />
                                                    <ListItemSecondaryAction>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => {
                                                                setSelectedUser(user);
                                                                setQuotaDialog(true);
                                                            }}
                                                        >
                                                            <IconEdit size={16} />
                                                        </IconButton>
                                                    </ListItemSecondaryAction>
                                                </ListItem>
                                            ))}
                                        </List>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                    </CardContent>
                )}

                {/* Retention Policies Tab */}
                {tabValue === 3 && (
                    <CardContent>
                        <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
                            <Typography variant="h4">Retention Policies</Typography>
                            <Button variant="contained" startIcon={<IconPlus />} onClick={() => setRetentionDialog(true)}>
                                Create Policy
                            </Button>
                        </Box>

                        <TableContainer component={Paper}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Policy Name</TableCell>
                                        <TableCell>Condition</TableCell>
                                        <TableCell>Action</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell align="right">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {retentionPolicies.map((policy) => (
                                        <TableRow key={policy.id}>
                                            <TableCell>{policy.name}</TableCell>
                                            <TableCell>{policy.condition}</TableCell>
                                            <TableCell>{policy.action}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={policy.status}
                                                    size="small"
                                                    color={policy.status === 'active' ? 'success' : 'default'}
                                                />
                                            </TableCell>
                                            <TableCell align="right">
                                                <IconButton size="small">
                                                    <IconEdit size={16} />
                                                </IconButton>
                                                <IconButton size="small" color="error">
                                                    <IconTrash size={16} />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <Alert severity="info" sx={{ mt: 3 }}>
                            Retention policies help manage storage costs by automatically archiving or deleting old files based on your
                            rules.
                        </Alert>
                    </CardContent>
                )}

                {/* Notifications Tab */}
                {tabValue === 4 && (
                    <CardContent>
                        <Typography variant="h4" gutterBottom>
                            Notification Settings
                        </Typography>

                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <Card variant="outlined">
                                    <CardContent>
                                        <Typography variant="h5" gutterBottom>
                                            Event Notifications
                                        </Typography>
                                        <TableContainer>
                                            <Table>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>Event</TableCell>
                                                        <TableCell align="center">Email</TableCell>
                                                        <TableCell align="center">Webhook</TableCell>
                                                        <TableCell align="center">In-App</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {notifications.map((notif) => (
                                                        <TableRow key={notif.id}>
                                                            <TableCell>{notif.event}</TableCell>
                                                            <TableCell align="center">
                                                                <Switch defaultChecked={notif.email} />
                                                            </TableCell>
                                                            <TableCell align="center">
                                                                <Switch defaultChecked={notif.webhook} />
                                                            </TableCell>
                                                            <TableCell align="center">
                                                                <Switch defaultChecked={notif.inApp} />
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </CardContent>
                                </Card>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Card variant="outlined">
                                    <CardContent>
                                        <Box display="flex" alignItems="center" gap={1} mb={2}>
                                            <IconMail />
                                            <Typography variant="h5">Email Configuration</Typography>
                                        </Box>
                                        <TextField fullWidth label="Notification Email" defaultValue="admin@example.com" sx={{ mb: 2 }} />
                                        <TextField fullWidth label="CC Recipients" helperText="Comma-separated emails" />
                                    </CardContent>
                                </Card>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Card variant="outlined">
                                    <CardContent>
                                        <Box display="flex" alignItems="center" gap={1} mb={2}>
                                            <IconWebhook />
                                            <Typography variant="h5">Webhook Configuration</Typography>
                                        </Box>
                                        <TextField
                                            fullWidth
                                            label="Webhook URL"
                                            defaultValue="https://api.example.com/webhooks/storage"
                                            sx={{ mb: 2 }}
                                        />
                                        <TextField
                                            fullWidth
                                            label="Secret Key"
                                            type="password"
                                            helperText="Used for webhook signature validation"
                                        />
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                    </CardContent>
                )}
            </Card>

            {/* Save Button */}
            <Box display="flex" justifyContent="flex-end" mt={3} gap={2}>
                <Button variant="outlined">Reset to Defaults</Button>
                <Button variant="contained" startIcon={<IconCheck />}>
                    Save Settings
                </Button>
            </Box>
        </Box>
    );
};

export default StorageSettings;
